import { useEffect, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import { db } from '@core/db/db'
import { PosEvent } from '@core/bus/events'
import {
  flushPendingModuleFlags,
  loadModuleEnabled,
  rollbackModuleEnabled,
  setModuleEnabled
} from '@core/modules/enable'
import type { ModuleUiDescriptor } from '@core/modules/types'
import { moduleToggle, sectionCard } from '@ui/admin'

/**
 * Карточка модуля в «Интеграциях».
 *
 * Галочка в заголовке — единственная точка включения модуля:
 * - снята  → блок сжимается до одной строки (контент не рендерится);
 * - стоит  → блок раскрывается целиком.
 *
 * До чтения meta контент не рендерится: иначе выключенный модуль успевает
 * смонтироваться (лишние запросы, мигание).
 */
export function ModuleCard({
  core,
  descriptor,
  canEdit
}: {
  core: AppCore
  descriptor: ModuleUiDescriptor
  canEdit?: boolean
}) {
  const [enabled, setEnabled] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void loadModuleEnabled({ db, descriptor, core })
      .then((next) => {
        if (cancelled) return
        setEnabled(next)
        setLoaded(true)
      })
      .catch(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [core, descriptor])

  // Флаг мог измениться на другом терминале — обновляем карточку.
  useEffect(() => {
    return core.bus.on(PosEvent.MODULE_ENABLED_CHANGED, (payload) => {
      if (payload.id !== descriptor.id) return
      setEnabled(payload.enabled)
      setLoaded(true)
      if (payload.pending) {
        setNote('Изменение сохранено локально и ожидает синхронизации с облаком')
      }
    })
  }, [core.bus, descriptor.id])

  const toggle = async (next: boolean) => {
    if (canEdit === false) return
    const previous = enabled
    setEnabled(next)
    setNote(null)
    try {
      const result = await setModuleEnabled({ db, descriptor, core }, next)
      if (!result.synced) {
        setNote(
          (result.pending
            ? 'Модуль сохранён локально, ожидает синхронизации с облаком'
            : 'Модуль сохранён локально, но облако отклонило изменение') +
            (result.error ? `: ${result.error}` : '')
        )
      }
    } catch (error) {
      // IndexedDB/квота или защита от выключения при hold-чеках.
      setEnabled(previous)
      rollbackModuleEnabled({ db, descriptor, core }, previous)
      setNote(error instanceof Error ? error.message : 'Не удалось сохранить состояние модуля')
    }
  }

  const retrySync = async () => {
    setNote(null)
    const result = await flushPendingModuleFlags({ db, core }, { force: true })
    if (!result.synced) {
      setNote(
        'Повтор не удался' + (result.error ? `: ${result.error}` : '')
      )
    }
  }

  const { Component } = descriptor

  return (
    <div
      className={sectionCard}
      data-testid={`module-card-${descriptor.id}`}
      data-collapsed={enabled ? 'false' : 'true'}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="m-0 text-big font-bold">{descriptor.title}</h3>
        <label
          className={moduleToggle}
          title={enabled ? 'Модуль включён' : 'Модуль выключен'}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => void toggle(e.target.checked)}
            aria-label={`Модуль «${descriptor.title}»`}
            data-testid={`module-toggle-${descriptor.id}`}
            disabled={canEdit === false}
            className="h-4 w-4 cursor-pointer accent-ok disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span>{enabled ? 'Вкл' : 'Выкл'}</span>
        </label>
      </div>

      {note && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <p className="mb-0 text-tiny text-amber-700">{note}</p>
          {note.includes('ожидает синхронизации') && canEdit !== false && (
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0 text-tiny font-semibold text-blue-700 underline"
              onClick={() => void retrySync()}
            >
              Повторить
            </button>
          )}
        </div>
      )}

      {loaded && enabled && (
        <div className="mt-3">
          <Component core={core} canEdit={canEdit} />
        </div>
      )}
    </div>
  )
}
